"use client";
import Image from "next/image";

import { useState } from "react";
import Link from "next/link";
import CalculatorShell from "@/components/CalculatorShell";
import CitationSection from "@/components/CitationSection";
import InputField from "@/components/InputField";
import SelectField from "@/components/SelectField";
import ResultCard from "@/components/ResultCard";
import { calcTile, type TileResult } from "@/lib/formulas";
import { CALCULATOR_SPECS } from "@/lib/calculator-info-data";
import FaqSection from "@/components/FaqSection";
import RelatedCalculators from "@/components/RelatedCalculators";

const TILE_PRESET_OPTIONS = [
  { value: "12x12", label: '12×12 in — standard floor tile' },
  { value: "18x18", label: '18×18 in — large floor tile' },
  { value: "24x24", label: '24×24 in — large format tile' },
  { value: "4x4", label: '4×4 in — kitchen backsplash' },
  { value: "3x6", label: '3×6 in — subway tile' },
  { value: "custom", label: 'Custom size' },
];

const PATTERN_OPTIONS = [
  { value: "straight", label: "Straight / Grid (+10% waste)" },
  { value: "offset", label: "Brick / Offset (+10% waste)" },
  { value: "diagonal", label: "Diagonal (+15% waste)" },
  { value: "herringbone", label: "Herringbone (+20% waste)" },
];

const GROUT_JOINT_OPTIONS = [
  { value: "1/16", label: "1/16 in — rectified tile (minimal)" },
  { value: "1/8", label: "1/8 in — standard floor" },
  { value: "3/16", label: "3/16 in — rustic / natural stone" },
  { value: "1/4", label: "1/4 in — wide joint" },
];

const TILE_PRESETS: Record<string, { w: number; h: number }> = {
  "12x12": { w: 12, h: 12 },
  "18x18": { w: 18, h: 18 },
  "24x24": { w: 24, h: 24 },
  "4x4":   { w: 4,  h: 4  },
  "3x6":   { w: 3,  h: 6  },
};

export default function CalculatorClient() {
  const [length, setLength] = useState("");
  const [width, setWidth] = useState("");
  const [tilePreset, setTilePreset] = useState("12x12");
  const [tileW, setTileW] = useState("12");
  const [tileH, setTileH] = useState("12");
  const [pattern, setPattern] = useState<"straight" | "diagonal" | "offset" | "herringbone">("straight");
  const [groutJoint, setGroutJoint] = useState("1/8");
  const [tilesPerBox, setTilesPerBox] = useState("");
  const [result, setResult] = useState<TileResult | null>(null);

  function handlePreset(preset: string) {
    setTilePreset(preset);
    if (preset !== "custom" && TILE_PRESETS[preset]) {
      setTileW(String(TILE_PRESETS[preset].w));
      setTileH(String(TILE_PRESETS[preset].h));
    }
    setResult(null);
  }

  const GROUT_JOINT_VALUES: Record<string, number> = {
    "1/16": 0.0625,
    "1/8":  0.125,
    "3/16": 0.1875,
    "1/4":  0.25,
  };

  function handleCalculate() {
    const res = calcTile({
      lengthFt: parseFloat(length) || 0,
      widthFt: parseFloat(width) || 0,
      tileWidthIn: parseFloat(tileW) || 12,
      tileHeightIn: parseFloat(tileH) || 12,
      pattern,
      groutJointIn: GROUT_JOINT_VALUES[groutJoint] ?? 0.125,
      tilesPerBox: parseFloat(tilesPerBox) || undefined,
    });
    setResult(res);
  }

  const wastePercent = { straight: 10, offset: 10, diagonal: 15, herringbone: 20 }[pattern];

  return (
    <CalculatorShell
      name="Tile Calculator"
      description="Calculate tiles and grout bags needed for floors or walls, with waste factor adjusted for your installation pattern."
      icon="tile"
      category="diy"
      accentColor="#0ea5e9"
      hasResult={result !== null}
      onCalculate={handleCalculate}
      disclaimer="Estimates account for standard waste factor. Herringbone or diagonal patterns require 15% waste — verify total tile count before purchasing."
      proTip="Keep 10-15 spare tiles in storage for future plumbing repairs or cracked tile replacement."
      inputsPanel={
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <InputField id="tile-area-length" label="Area Length" unit="ft" type="number" min="0" step="0.5" placeholder="15" value={length} onChange={(e) => { setLength(e.target.value); setResult(null); }} />
            <InputField id="tile-area-width" label="Area Width" unit="ft" type="number" min="0" step="0.5" placeholder="10" value={width} onChange={(e) => { setWidth(e.target.value); setResult(null); }} />
          </div>
          <SelectField id="tile-preset" label="Tile Size" value={tilePreset} onChange={(e) => handlePreset(e.target.value)} options={TILE_PRESET_OPTIONS} />
          {tilePreset === "custom" && (
            <div className="grid grid-cols-2 gap-3">
              <InputField id="tile-custom-w" label="Tile Width" unit="in" type="number" min="0" step="0.25" value={tileW} onChange={(e) => { setTileW(e.target.value); setResult(null); }} />
              <InputField id="tile-custom-h" label="Tile Height" unit="in" type="number" min="0" step="0.25" value={tileH} onChange={(e) => { setTileH(e.target.value); setResult(null); }} />
            </div>
          )}
          <SelectField id="tile-pattern" label="Installation Pattern" value={pattern} onChange={(e) => { setPattern(e.target.value as typeof pattern); setResult(null); }} options={PATTERN_OPTIONS} />
          <SelectField id="grout-joint" label="Grout Joint Width" value={groutJoint} onChange={(e) => { setGroutJoint(e.target.value); setResult(null); }} options={GROUT_JOINT_OPTIONS} />
          <InputField id="tiles-per-box" label="Tiles per Box (optional)" type="number" min="0" step="1" placeholder="e.g. 12" value={tilesPerBox} onChange={(e) => { setTilesPerBox(e.target.value); setResult(null); }} hint="Check box label — varies by manufacturer" />
        </div>
      }
      resultsPanel={
        result && (
          <div className="space-y-3">
            <ResultCard label={`Tiles Needed (+${wastePercent}% waste)`} value={result.tilesTotal} unit="tiles" highlight size="lg" sub={`${result.tilesBase} tiles exact · ${tileW}×${tileH} in`} />
            <div className="grid grid-cols-2 gap-3">
              <ResultCard label="Area" value={result.areaSqFt} unit="sq ft" accent />
              <ResultCard label="Area" value={result.areaSqM} unit="m²" accent />
            </div>
            {result.boxesNeeded !== undefined && (
              <ResultCard label="Boxes to Purchase" value={result.boxesNeeded} unit="boxes" highlight sub={`${tilesPerBox} tiles/box`} />
            )}
            <ResultCard label="Grout Bags (estimated)" value={result.groutBags} unit="bags" sub={`~50 sq ft coverage per bag · Verify with manufacturer`} />
          </div>
        )
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center text-[var(--ink)]">
        {/* Row 1 - Left: Buying Guide */}
        <div className="space-y-4 md:order-1">
          <h3 className="text-xl font-bold tracking-tight text-[var(--ink)]">
            Tile & Grout Selection & Buying Guide
          </h3>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            Selecting tiles involves matching the material to its intended application. Check the Porcelain Enamel Institute (PEI) rating: Class III is standard for residential <Link href="/articles/vinyl-vs-laminate-vs-hardwood-flooring/" className="text-[var(--accent)] hover:underline font-semibold">flooring</Link>, while Class IV or V is required for high-traffic entryways or commercial use. Ensure your selection matches wet area safety standards, such as a Dynamic Coefficient of Friction (DCOF) of at least <strong>{CALCULATOR_SPECS.tile.buyingGuide.slipResistanceDCOF.value}</strong> for shower floors or bathrooms. Ceramic tiles absorb more water than porcelain, making porcelain tiles the standard choice for outdoor applications or high-moisture rooms.
          </p>
        </div>

        {/* Row 1 - Right: Buying Guide Image */}
        <div className="relative rounded-xl overflow-hidden border border-[var(--line)] aspect-[3/2] w-full bg-[var(--surface-muted)] md:order-2">
          <Image src="/images/tile_material_guide.webp" alt="Showroom board displaying ceramic and porcelain tiles of various colors and designs" fill sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 576px" className="object-cover" priority />
        </div>

        {/* Row 2 - Left: Site Prep Image */}
        <div className="relative rounded-xl overflow-hidden border border-[var(--line)] aspect-[3/2] w-full bg-[var(--surface-muted)] md:order-3">
          <Image src="/images/tile_site_prep.webp" alt="Spreading thinset mortar on plywood subfloor with a notched trowel for cement backerboard installation" fill sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 576px" className="object-cover" loading="lazy" />
        </div>

        {/* Row 2 - Right: Site Prep Guide */}
        <div className="space-y-4 md:order-4">
          <h3 className="text-xl font-bold tracking-tight text-[var(--ink)]">
            Tile & Grout Substrate Prep & Installation
          </h3>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            Tile installations require a perfectly flat, <Link href="/articles/flooring-installation-cost-per-square-foot/" className="text-[var(--accent)] hover:underline font-semibold">deflection-free subfloor</Link>. For large format tiles (any side 15 inches or longer), substrate deviation must not exceed <strong>{CALCULATOR_SPECS.tile.sitePrep.substrateFlatnessTolerance.value}</strong>. Level subfloors with self-leveling underlayment or cement backerboard if necessary. Use modified thinset mortar for porcelain tiles to ensure proper adhesion. When laying the tiles, always plan your layout from the center of the room outward to avoid tiny, hard-to-cut slivers of tile at the walls, and leave expansion joints around the perimeter.
          </p>
        </div>

      </div>

      <CitationSection
        standardCode={CALCULATOR_SPECS.tile.industryStandards.lippageToleranceMax.standardCode}
        sourceName={CALCULATOR_SPECS.tile.industryStandards.lippageToleranceMax.sourceName}
        sourceUrl={CALCULATOR_SPECS.tile.industryStandards.lippageToleranceMax.sourceUrl}
        paragraphs={[
          <p key="p1">
            Lippage is the vertical offset between the edges of adjacent tiles, resulting in an uneven floor surface that is unsightly, difficult to clean, and a potential tripping hazard. According to the <b>{CALCULATOR_SPECS.tile.industryStandards.lippageToleranceMax.standardCode}</b> specifications set by ANSI, the maximum allowable lippage between adjacent ceramic or porcelain tiles is strictly limited to <b>{CALCULATOR_SPECS.tile.industryStandards.lippageToleranceMax.value}</b>. This restriction becomes challenging when installing modern large-format tiles—those with any side 15 inches or longer—because large tiles naturally warp slightly during kiln firing, bowing up at the center.
          </p>,
          <p key="p2">
            To offset this inherent bowing, installers should avoid a 50% offset brick pattern, opting instead for a 33% offset layout that prevents the highest center point of one tile from aligning directly with the lowest corners of adjacent tiles. Furthermore, subfloors must meet TCNA flatness tolerances of 1/8 inch deviation over 10 feet. Using wedge-and-clip mechanical tile leveling systems helps hold tiles flat and flush while the thinset mortar cures, and maintaining minimum grout joint widths of 1/16 inch for rectified tile allows enough flex to absorb minor substrate vibrations.
          </p>
        ]}
        keyRequirements={[
          "Lippage between adjacent tiles must not exceed 1/32 inch (0.8 mm) per ANSI A108.02.",
          "Avoid 50% offset layouts for large-format tiles; use a maximum 33% offset layout instead.",
          "Subfloor flatness deviation must be less than 1/8 inch over a 10-foot span.",
          "Use a mechanical leveling wedge-and-clip system to maintain flatness while thinset cures."
        ]}
      />

      <FaqSection calculatorId="tile" />
      <RelatedCalculators calculatorId="tile" />
    </CalculatorShell>
  );
}
